import React from 'react';
import { motion } from 'framer-motion';
import { 
  Cpu, 
  Shield, 
  FlaskConical, 
  TrendingUp, 
  Compass, 
  Zap, 
  Satellite,
  ArrowRight,
  Binary,
  Microscope
} from 'lucide-react';
import { ApplicationCard } from '@/components/Cards';
import { quantumApplications } from '@/data/index';
import { IMAGES } from '@/assets/images';

const iconMap: Record<string, React.ReactNode> = {
  Shield: <Shield className="w-6 h-6" />,
  FlaskConical: <FlaskConical className="w-6 h-6" />,
  TrendingUp: <TrendingUp className="w-6 h-6" />,
  Compass: <Compass className="w-6 h-6" />,
  Zap: <Zap className="w-6 h-6" />,
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Applications() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.QUANTUM_COMPUTING_3} 
            alt="Quantum Computing Infrastructure" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 border border-primary/20">
              The Second Quantum Revolution
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-foreground">
              Quantum <span className="text-primary">Applications</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
              Moving beyond theoretical physics into a new era of industrial utility, from secure communication to life-saving drug discovery.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-24 container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-xl">
            <h2 className="text-3xl font-bold mb-4">Industrial Solutions</h2>
            <p className="text-muted-foreground">
              Explore how quantum phenomena like superposition and entanglement are being harnessed to solve some of the world's most complex challenges.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-mono text-muted-foreground">
            <Binary className="w-4 h-4 text-primary" />
            <span>QBIT_COORDINATES_ESTABLISHED</span>
          </div>
        </div>

        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {quantumApplications.map((app) => (
            <motion.div key={app.id} variants={fadeInUp}>
              <ApplicationCard application={app} />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Feature Section: Healthcare */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-accent/10 text-accent">
                  <Microscope className="w-8 h-8" />
                </div>
                <h2 className="text-4xl font-bold">Biotech & Medicine</h2>
              </div>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Traditional supercomputers struggle to simulate even simple molecules. Quantum systems allow us to map complex chemical reactions in real-time, accelerating drug discovery from years to weeks.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Precision molecular docking simulations",
                  "Accelerated mRNA vaccine development",
                  "Personalized genomic sequencing at scale",
                  "Novel materials for biological implants"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground/80">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="p-6 rounded-2xl border border-border bg-card/50 backdrop-blur-sm">
                <p className="text-sm italic text-muted-foreground">
                  "The Bahrain-SandboxAQ partnership is a testament to the Kingdom's commitment to using quantum-inspired AI for regional health breakthroughs."
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl"
            >
              <img 
                src={IMAGES.RESEARCH_COLLABORATION_2} 
                alt="Scientific Collaboration" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security & Future */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full z-0 opacity-10">
          <img 
            src={IMAGES.PARTICLE_PHYSICS_4} 
            alt="Quantum Particles" 
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h2 className="text-4xl font-bold mb-8">The Security Imperative</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
                    <Satellite className="w-5 h-5 text-primary" />
                    Quantum Internet
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Building unbreakable communication networks using entangled photons for satellite-to-ground secure links.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-accent/5 border border-accent/10">
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
                    <Cpu className="w-5 h-5 text-accent" />
                    Post-Quantum Standards
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Implementing mathematical algorithms that are resistant to attacks from both classical and future quantum computers.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  As quantum computers evolve, traditional encryption becomes vulnerable. Bahrain is leading the region by adopting Post-Quantum Cryptography (PQC) today, ensuring national data remains secure for the next century.
                </p>
                <button className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                  View Bahrain's Security Roadmap <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            Ready for the Quantum Era?
          </h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-12">
            The transition is happening now. Discover how your industry can benefit from the computational power of the 21st century.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-primary font-bold rounded-full hover:bg-white/90 transition-colors shadow-lg">
              Download whitepaper
            </button>
            <button className="px-8 py-4 bg-primary-foreground/10 border border-white/20 text-white font-bold rounded-full hover:bg-white/10 transition-colors">
              Contact an Expert
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
